import { useMemo } from 'react'
import { AuthError, AuthErrorCode } from '../../errors'
import { apiFetch } from '../../helpers/apiFetch'
import { missingRefreshToken } from '../../helpers/missingRefreshToken'
import { resolveTraceHeaders } from '../../helpers/resolveTraceHeaders'
import { isTokenExpired } from '../../helpers/isExpired'
import type { AuthConfig } from '../../types'

const SWITCH_ORGANIZATION_MUTATION = `mutation SwitchOrganization($data: AuthMutationsSwitchOrganizationInput!) {
  auth {
    switchOrganization(data: $data) {
      refreshToken
      accessToken
    }
  }
}`

type AuthErrorProps = Parameters<typeof AuthError.create>[0]

// Errors that mean the session itself is invalid — clear tokens and force re-auth.
const API_ERRORS_THAT_INVALIDATE_SESSION: Record<string, AuthErrorProps> = {
  UNAUTHENTICATED: {
    code: AuthErrorCode.refreshTokenInvalid,
    message: 'user is not authenticated',
  },
}

// Errors that mean "this switch is not allowed" — notify, but keep current session.
// Own code (not notAuthorized, which the token refresh also raises after clearing
// the session); the server message and `extensions.reason` say why.
const API_ERRORS_THAT_REJECT_SWITCH: Record<string, AuthErrorProps> = {
  FORBIDDEN: {
    code: AuthErrorCode.switchRejected,
    message: 'switch not allowed',
  },
}

export const useGenerateSwitchOrganizationAction = (config: AuthConfig) => {
  const {
    setAccessToken,
    getRefreshToken,
    setRefreshToken,
    onError,
    apiUrl,
    getTraceHeaders,
  } = config

  return useMemo(() => {
    return async (tid: number): Promise<string | null> => {
      // Logged out: fail at once, before the trace getter does any device I/O
      if (!getRefreshToken()) throw missingRefreshToken()
      // Resolve the headers first: the token is read right before it is sent
      const headers = await resolveTraceHeaders(getTraceHeaders)
      const refreshToken = getRefreshToken()
      if (!refreshToken) throw missingRefreshToken()

      let response: Awaited<ReturnType<typeof apiFetch>>
      try {
        response = await apiFetch(
          apiUrl,
          {
            operationName: 'SwitchOrganization',
            variables: { data: { tid, refreshToken } },
            query: SWITCH_ORGANIZATION_MUTATION,
          },
          headers,
        )
      } catch (err) {
        onError?.(err as AuthError)
        return null
      }

      const { errors } = response
      if (errors?.length) {
        if (isTokenExpired(refreshToken)) {
          setRefreshToken(null)
          setAccessToken(null)
          onError?.(
            AuthError.create({
              code: AuthErrorCode.refreshTokenExpired,
              message: 'refresh token has expired',
            }),
          )
          return null
        }
        for (const err of errors) {
          const apiCode = err.extensions?.code ?? ''
          const invalidateError = API_ERRORS_THAT_INVALIDATE_SESSION[apiCode]
          if (invalidateError) {
            setRefreshToken(null)
            setAccessToken(null)
            onError?.(AuthError.create(invalidateError))
            return null
          }
          const rejectError = API_ERRORS_THAT_REJECT_SWITCH[apiCode]
          if (rejectError) {
            onError?.(
              AuthError.create({
                ...rejectError,
                message: err.message || rejectError.message,
                reason: err.extensions?.reason,
              }),
            )
            return null
          }
        }
        onError?.(
          AuthError.create({
            code: AuthErrorCode.refreshFailed,
            message: errors.map((e) => e.message).join('\n'),
          }),
        )
        return null
      }

      const tokens = response.data?.auth?.switchOrganization
      if (!tokens) {
        onError?.(
          AuthError.create({
            code: AuthErrorCode.refreshTokenNotFound,
            message: 'missing tokens in switchOrganization response',
          }),
        )
        return null
      }

      setRefreshToken(tokens.refreshToken || null)
      setAccessToken(tokens.accessToken || null)
      return tokens.accessToken ?? null
    }
  }, [
    apiUrl,
    getRefreshToken,
    setRefreshToken,
    setAccessToken,
    onError,
    getTraceHeaders,
  ])
}
